import memoize from 'memoizee';
import { contract as currentContract, infura_url } from '@/contract.js'
import Web3 from 'web3';
import { flattenArray } from '@/utils/Array';
// import { multiCall } from '../Calls';
// import { factoryRegistry_abi, factoryPool_abi } from '@/constants/abis';
import config from '@/constants/config';
import pools from '@/constants/pools';
import REFERENCE_ASSETS from '@/constants/reference-assets.js';

const validatePoolsData = (factoryPools) => factoryPools.forEach(({
    id,
    referenceAsset,
}) => {
    if (typeof referenceAsset === 'undefined') {
        throw new Error(`Factory pool data error: pool #${id} is missing a referenceAsset`);
    }
});

const getFactoryPools = memoize(async () => {
    const hasFactoryRegistry = !!config.factoryRegistryAddress;
    if (!hasFactoryRegistry) return [];

    // Dynamically import
    const { multiCall } = require('../Calls');
    const { factoryRegistry_abi, factoryPool_abi } = require('@/constants/abis');

    const web3 = currentContract.web3 || new Web3(infura_url);
    const registryContract = new web3.eth.Contract(factoryRegistry_abi, config.factoryRegistryAddress);

    const poolCount = Number(await registryContract.methods.pool_count().call());
    const poolIds = Array(poolCount).fill(0).map((_, i) => i);

    const poolAddresses = await multiCall(poolIds.map((id) => ({
        contract: registryContract,
        methodName: 'pool_list',
        params: [id],
    })));

    const poolData = await multiCall(flattenArray(poolAddresses.map((address, id) => {
        const poolContract = new web3.eth.Contract(factoryPool_abi, address);

        return [{
            contract: registryContract,
            methodName: 'get_coins', // address[2]
            params: [address],
            metaData: { poolId: id, type: 'coinsAddresses' },
        }, {
            contract: registryContract,
            methodName: 'get_underlying_coins', // address[8]
            params: [address],
            metaData: { poolId: id, type: 'underlyingCoinsAddresses' },
        }, {
            contract: registryContract,
            methodName: 'get_decimals', // address[2]
            params: [address],
            metaData: { poolId: id, type: 'decimals' },
        }, {
            contract: registryContract,
            methodName: 'get_underlying_decimals', // address[8]
            params: [address],
            metaData: { poolId: id, type: 'underlyingDecimals' },
        }, {
            contract: poolContract,
            methodName: 'name',
            metaData: { poolId: id, type: 'name' },
        }, {
            contract: poolContract,
            methodName: 'symbol',
            metaData: { poolId: id, type: 'symbol' },
        }, {
            contract: poolContract,
            methodName: 'totalSupply',
            metaData: { poolId: id, type: 'totalSupply' },
        }];
    })));

    const [
        factoryApysResult,
        { data: { poolData: otherPoolData } },
    ] = await Promise.all([
        (await fetch('https://api.curve.finance/api/getFactoryAPYs')).json(),
        (await fetch('https://api.curve.finance/api/getFactoryPools')).json(),
    ]);

    const apyData = factoryApysResult?.data?.poolDetails || [];

    const emptyData = poolIds.map((id) => ({ id }));
    const data = poolData.reduce((accu, { data, metaData: { poolId, type } }) => {
        const poolInfo = accu[poolId];
        const apyInfo = apyData[poolId];
        const otherPoolInfo = otherPoolData[poolId];

        accu[poolId] = {
            ...poolInfo,
            name: poolInfo.name?.replace(/Curve\.fi Factory \w+ Metapool: /, ''),
            address: poolAddresses[poolId],
            [type]: data,
            apy: apyInfo?.apy,
            volume: apyInfo?.volume,
            coins: [
                otherPoolInfo.token,
                otherPoolInfo.lpToken,
            ],
        };

        return accu;
    }, emptyData);

    const augmentedData = data.map((poolInfo) => {
        const lastCoin = poolInfo.coinsAddresses[poolInfo.coinsAddresses.length - 1];
        const referenceAsset = (
            lastCoin === '0x6c3F90f043a72FA612cbac8115EE7e52BDe6E490' ? REFERENCE_ASSETS.USD :
                lastCoin === '0x075b1bb99792c9E1041bA13afEf80C91a1e70fB3' ? REFERENCE_ASSETS.BTC :
                    undefined
        );

        return {
            ...poolInfo,
            referenceAsset,
            underlyingDecimals: poolInfo.underlyingDecimals.filter((v) => v !== '0'),
            underlyingCoinsAddresses: poolInfo.underlyingCoinsAddresses.filter((v) => v !== '0x0000000000000000000000000000000000000000'),
        };
    });

    const filteredData = augmentedData.filter(({ address }) => {
        const lcAddress = address.toLowerCase();
        const isMainUiPool = pools.some(({ addresses }) => addresses.swap.toLowerCase() === lcAddress);

        return !isMainUiPool;
    })

    validatePoolsData(filteredData);

    return filteredData;
}, {
    promise: true,
    maxAge: 60 * 1000, // 1m
});

export default getFactoryPools;
